import { createI18n } from 'vue-i18n'
import zh from './zh'
import en from './en'

// Vuetify locale messages (built-in component strings)
import { en as vuetifyEn, zhHans as vuetifyZhHans } from 'vuetify/locale'

/**
 * The only locale codes this app persists or accepts. Anything writing to
 * `localStorage.locale` must use one of these — a value outside this set is
 * silently discarded by `detectLocale()`, which looks like "the language
 * switcher does nothing" rather than like an error.
 */
export const SUPPORTED_LOCALES = ['zh', 'en'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

function detectLocale(): SupportedLocale {
  const stored = localStorage.getItem('locale')
  if (isSupportedLocale(stored)) return stored

  const browserLang = navigator.language.toLowerCase()
  if (browserLang.startsWith('zh')) return 'zh'
  return browserLang.startsWith('en') ? 'en' : 'zh'
}

const locale = detectLocale()
localStorage.setItem('locale', locale)

const i18n = createI18n({
  legacy: false,
  locale,
  fallbackLocale: 'zh',
  messages: { zh: zh.messages, en: en.messages },
})

export function setLocale(lang: SupportedLocale) {
  i18n.global.locale.value = lang
  localStorage.setItem('locale', lang)
}

export function getVuetifyLocaleMessages() {
  return {
    zhHans: { ...vuetifyZhHans, ...zh.vuetify },
    en: { ...vuetifyEn, ...en.vuetify },
  }
}

export function getVuetifyLocale(): string {
  return locale === 'zh' ? 'zhHans' : 'en'
}

export default i18n
